import { Link } from "react-router-dom";
import { Check, Eye, X, RotateCcw, Inbox } from "lucide-react";

const TYPE_LABEL = {
  correction: "Correction",
  information: "Additional info",
  provenance: "Provenance / source",
  rights: "Rights / permissions",
};

const STATUS_STYLE = {
  pending: "text-[#C08A3E] border-[#C08A3E]/40",
  reviewed: "text-[#6FA8DC] border-[#6FA8DC]/40",
  resolved: "text-[#7FB069] border-[#7FB069]/40",
  dismissed: "text-[#8A8F98] border-white/15",
};

const FILTERS = ["pending", "reviewed", "resolved", "dismissed", "all"];

export const SubmissionsTab = ({ contributions, counts, filter, setFilter, updateContribution }) => (
  <div>
    <div className="flex flex-wrap gap-3 mb-6">
      {FILTERS.map((f) => (
        <button key={f} data-testid={`submissions-filter-${f}`} onClick={() => setFilter(f)}
          className={`px-4 py-2 text-sm capitalize border transition-colors ${
            filter === f ? "bg-[#C5A059] text-[#0A0B0E] border-[#C5A059]" : "text-[#8A8F98] border-white/10 hover:border-[#C5A059]/40"
          }`}>
          {f}{f !== "all" && counts?.[f] !== undefined ? ` (${counts[f]})` : ""}
        </button>
      ))}
    </div>

    {(!contributions || contributions.length === 0) ? (
      <div data-testid="submissions-empty" className="border border-white/10 bg-[#121418] p-10 text-center text-[#8A8F98]">
        <Inbox size={28} className="mx-auto mb-4 text-[#C5A059]" strokeWidth={1.5} />
        No submissions in this view.
      </div>
    ) : (
      <div className="space-y-4">
        {contributions.map((c) => (
          <div key={c.id} data-testid={`submission-${c.id}`} className="border border-white/10 bg-[#121418] p-5">
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <span className="text-[10px] tracking-widest uppercase text-[#C5A059]">{TYPE_LABEL[c.submission_type] || c.submission_type}</span>
              <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 border ${STATUS_STYLE[c.status] || STATUS_STYLE.dismissed}`}>{c.status}</span>
              <span className="text-xs text-[#8A8F98] ml-auto">{c.created_at ? new Date(c.created_at).toLocaleString() : ""}</span>
            </div>
            {c.video_title && (
              <p className="text-sm text-[#8A8F98] mb-2">
                Re: {c.video_id
                  ? <Link to={`/watch/${c.video_id}`} className="text-[#C5A059] hover:underline">{c.video_title}</Link>
                  : <span className="text-[#EBE9E1]">{c.video_title}</span>}
              </p>
            )}
            <p className="text-[#EBE9E1] text-sm leading-relaxed whitespace-pre-wrap">{c.message}</p>
            {(c.name || c.email) && (
              <p className="text-xs text-[#8A8F98] mt-3">— {c.name || "Anonymous"}{c.email ? ` · ${c.email}` : ""}</p>
            )}
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/5">
              <Action testid={`submission-review-${c.id}`} icon={Eye} label="Reviewed" onClick={() => updateContribution(c.id, "reviewed")} />
              <Action testid={`submission-resolve-${c.id}`} icon={Check} label="Resolved" onClick={() => updateContribution(c.id, "resolved")} />
              <Action testid={`submission-dismiss-${c.id}`} icon={X} label="Dismiss" onClick={() => updateContribution(c.id, "dismissed")} />
              {c.status !== "pending" && (
                <Action testid={`submission-reopen-${c.id}`} icon={RotateCcw} label="Reopen" onClick={() => updateContribution(c.id, "pending")} />
              )}
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const Action = ({ testid, icon: Icon, label, onClick }) => (
  <button data-testid={testid} onClick={onClick}
    className="flex items-center gap-1.5 text-xs text-[#8A8F98] hover:text-[#C5A059] border border-white/10 hover:border-[#C5A059]/40 px-3 py-1.5 transition-colors">
    <Icon size={13} /> {label}
  </button>
);
